class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

/*
 * BioVance: apertura confiable de menús del header (dropdown de escritorio y drawer móvil).
 *
 * Dawn mezcla el toggle nativo de <details> con estado en JS:
 *  - Drawer móvil: al cerrar, `open` se quita ~400 ms después (requestAnimationFrame). Un toque
 *    dentro de esa ventana invierte el estado nativo y el menú "no responde".
 *  - Dropdown de escritorio: en Safari/táctil el <summary> no recibe foco al hacer clic y el cierre
 *    por `focusout` puede ejecutarse justo después de abrir.
 *  - Restauración desde bfcache (botón Atrás) deja menús a medio estado.
 *
 * Este script:
 *  1. Anula el toggle nativo de <summary> en el header y aplica el estado de forma explícita.
 *     Excepción: un enlace real dentro del <summary> (p. ej. "Ofertas" -> /collections/ofertas)
 *     navega normalmente; solo la flecha abre el submenú.
 *  2. Hace cancelable el cierre animado del drawer (reabrir durante la animación funciona).
 *  3. Ignora el cierre por `focusout` mientras hay un clic/toque en curso dentro del menú.
 *  4. Resetea todos los menús al volver desde bfcache.
 *  5. En escritorio con mouse, los menús cuyo título es un enlace se abren al pasar el cursor,
 *     para que sus subcategorías sigan siendo fáciles de descubrir.
 * Va al final de details-disclosure.js (se carga en todas las páginas, después de global.js).
 */
(function () {
  if (window.__bvHeaderMenuReliability) return;
  window.__bvHeaderMenuReliability = true;

  var HEADER_MENU_SUMMARY = 'header-menu > details > summary';
  var DRAWER_SUMMARY = 'header-drawer summary';

  // ---- 2. Cierre animado cancelable (MenuDrawer.closeAnimation se invoca vía prototipo) ----
  function patchMenuDrawer() {
    var MenuDrawer = customElements.get('menu-drawer');
    if (!MenuDrawer || MenuDrawer.prototype.__bvPatched) return;
    MenuDrawer.prototype.__bvPatched = true;

    MenuDrawer.prototype.closeAnimation = function (detailsElement) {
      var token = {};
      detailsElement.__bvCloseToken = token;
      var animationStart;

      var handleAnimation = function (time) {
        if (detailsElement.__bvCloseToken !== token) return; // reabierto: se cancela el cierre
        if (animationStart === undefined) animationStart = time;

        if (time - animationStart < 400) {
          window.requestAnimationFrame(handleAnimation);
          return;
        }

        detailsElement.__bvCloseToken = null;
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]') && typeof trapFocus === 'function') {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      };

      window.requestAnimationFrame(handleAnimation);

      // Respaldo si rAF se retrasa o pausa (pestaña en segundo plano, hilo principal saturado).
      setTimeout(function () {
        if (detailsElement.__bvCloseToken !== token) return;
        detailsElement.__bvCloseToken = null;
        detailsElement.removeAttribute('open');
      }, 700);
    };
  }

  function isClosing(details) {
    return Boolean(details.__bvCloseToken);
  }

  function cancelClosing(details) {
    details.__bvCloseToken = null;
  }

  // ---- 3. Protección contra cierre por focusout durante un clic/toque ----
  function patchDetailsDisclosure() {
    var DetailsDisclosure = customElements.get('details-disclosure');
    var HeaderMenu = customElements.get('header-menu');
    [DetailsDisclosure, HeaderMenu].forEach(function (Klass) {
      if (!Klass || Object.prototype.hasOwnProperty.call(Klass.prototype, '__bvPatched')) return;
      var originalClose = Klass.prototype.close;
      Klass.prototype.close = function () {
        if (this.__bvPressing) return;
        originalClose.apply(this, arguments);
      };
      Klass.prototype.__bvPatched = true;
    });
  }

  document.addEventListener(
    'pointerdown',
    function (event) {
      var menu = event.target.closest && event.target.closest('header-menu');
      if (!menu) return;
      menu.__bvPressing = true;
      clearTimeout(menu.__bvPressTimer);
      menu.__bvPressTimer = setTimeout(function () {
        menu.__bvPressing = false;
      }, 600);
    },
    true
  );

  // ---- 1. Toggle explícito ----
  // Fase de captura: se anula el toggle nativo y se decide la intención ANTES de los handlers de Dawn.
  document.addEventListener(
    'click',
    function (event) {
      var summary = event.target.closest && event.target.closest('summary');
      if (!summary) return;

      // Un enlace real dentro del <summary> navega: no se toca el evento.
      var innerLink = event.target.closest('a[href]');
      if (innerLink && summary.contains(innerLink)) return;

      var details = summary.parentElement;

      if (summary.matches(HEADER_MENU_SUMMARY)) {
        event.preventDefault();
        event.__bvHeaderMenu = { details: details, open: !details.hasAttribute('open') };
        return;
      }

      if (summary.matches(DRAWER_SUMMARY)) {
        var drawer = summary.closest('header-drawer');
        if (!drawer || !drawer.mainDetailsToggle) return;

        event.preventDefault();
        var effectivelyOpen = details.hasAttribute('open') && !isClosing(details);

        if (!effectivelyOpen && isClosing(details)) {
          // Reapertura durante la animación de cierre: se cancela y se parte de "cerrado"
          // para que MenuDrawer.onSummaryClick tome la rama de apertura.
          cancelClosing(details);
          details.removeAttribute('open');
          details.classList.remove('menu-opening');
        }

        event.__bvDrawer = { details: details, open: !effectivelyOpen, isMain: details === drawer.mainDetailsToggle };
      }
    },
    true
  );

  function openHeaderMenu(details) {
    var summary = details.querySelector('summary');
    document.querySelectorAll('header-menu > details[open]').forEach(function (other) {
      if (other !== details) {
        other.removeAttribute('open');
        var s = other.querySelector('summary');
        if (s) s.setAttribute('aria-expanded', 'false');
      }
    });
    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
  }

  function closeHeaderMenu(details) {
    details.removeAttribute('open');
    var summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
  }

  // Fase de burbuja en document: los handlers de Dawn ya corrieron; se aplica el estado final.
  document.addEventListener('click', function (event) {
    // Fin del clic: la protección se libera después de cualquier focusout ya encolado (FIFO).
    var pressedMenu = event.target.closest && event.target.closest('header-menu');
    if (pressedMenu && pressedMenu.__bvPressing) {
      setTimeout(function () {
        pressedMenu.__bvPressing = false;
      }, 0);
    }

    var hm = event.__bvHeaderMenu;
    if (hm) {
      var details = hm.details;
      var summary = details.querySelector('summary');
      if (hm.open) {
        openHeaderMenu(details);
        // Safari no enfoca <summary> al hacer clic; se enfoca para que el cierre por focusout sea consistente.
        try {
          summary.focus({ preventScroll: true });
        } catch (e) {
          summary.focus();
        }
      } else {
        closeHeaderMenu(details);
      }
      return;
    }

    var dr = event.__bvDrawer;
    if (dr && dr.open) {
      cancelClosing(dr.details);
      dr.details.setAttribute('open', '');
    }
  });

  // ---- 5. Apertura al pasar el cursor para menús cuyo título es enlace ----
  function linkParentMenu(el) {
    var menu = el && el.closest && el.closest('header-menu');
    if (!menu) return null;
    return menu.querySelector(':scope > details > summary.bv-menu-parent') ? menu : null;
  }

  document.addEventListener('pointerover', function (event) {
    if (event.pointerType !== 'mouse') return;
    var menu = linkParentMenu(event.target);
    if (!menu) return;
    clearTimeout(menu.__bvHoverTimer);
    if (event.relatedTarget && menu.contains(event.relatedTarget)) return;
    var details = menu.querySelector(':scope > details');
    if (!details.hasAttribute('open')) {
      openHeaderMenu(details);
      menu.__bvOpenedByHover = true;
    }
  });

  document.addEventListener('pointerout', function (event) {
    if (event.pointerType !== 'mouse') return;
    var menu = linkParentMenu(event.target);
    if (!menu || !menu.__bvOpenedByHover) return;
    if (event.relatedTarget && menu.contains(event.relatedTarget)) return;
    menu.__bvHoverTimer = setTimeout(function () {
      menu.__bvOpenedByHover = false;
      closeHeaderMenu(menu.querySelector(':scope > details'));
    }, 250);
  });

  // ---- 4. Reset al restaurar desde bfcache ----
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;

    document.querySelectorAll('header-menu > details[open]').forEach(function (details) {
      details.removeAttribute('open');
      var s = details.querySelector('summary');
      if (s) s.setAttribute('aria-expanded', 'false');
    });

    document.querySelectorAll('header-drawer details').forEach(function (details) {
      cancelClosing(details);
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
      var s = details.querySelector('summary');
      if (s) s.setAttribute('aria-expanded', 'false');
    });
    document.querySelectorAll('header-drawer .submenu-open').forEach(function (el) {
      el.classList.remove('submenu-open');
    });

    var header = document.querySelector('.section-header');
    if (header) header.classList.remove('menu-open');
    document.body.classList.remove('overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop');
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
  });

  function init() {
    patchMenuDrawer();
    patchDetailsDisclosure();
  }

  init();
  // Por si este script se ejecuta antes de que se definan los custom elements.
  customElements.whenDefined('menu-drawer').then(patchMenuDrawer);
  customElements.whenDefined('header-menu').then(patchDetailsDisclosure);
})();
